import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, MapPin, Truck, DollarSign, ToggleLeft, ToggleRight, CalendarOff, Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLocations = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    latitude: '',
    longitude: '',
    traffic_frequency: '',
    price_per_week: '',
    price_per_month: '',
    assigned_trailer_id: '',
    images: [],
    unavailable_from: '',
    unavailable_until: '',
    min_booking_lead_days: 28,
    has_existing_print: false,
  });
  const [saving, setSaving] = useState(false);
  const [waitlistStats, setWaitlistStats] = useState({});

  useEffect(() => {
    fetchLocations();
    fetchTrailers();
    fetchWaitlistStats();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrailers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/trailers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTrailers(data);
    } catch (error) {
      console.error('Error fetching trailers:', error);
    }
  };

  const fetchWaitlistStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/waitlist/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const map = {};
      (data.by_location || []).forEach(entry => { map[entry.location_id] = entry.count; });
      setWaitlistStats(map);
    } catch (error) {
      console.error('Error fetching waitlist stats:', error);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setFormData({
        name: location.name,
        address: location.address,
        description: location.description || '',
        latitude: location.latitude.toString(),
        longitude: location.longitude.toString(),
        traffic_frequency: location.traffic_frequency || '',
        price_per_week: location.price_per_week.toString(),
        price_per_month: location.price_per_month.toString(),
        assigned_trailer_id: location.assigned_trailer_id || 'none',
        images: location.images || [],
        unavailable_from: location.unavailable_from || '',
        unavailable_until: location.unavailable_until || '',
        min_booking_lead_days: location.min_booking_lead_days ?? 28,
        has_existing_print: location.has_existing_print ?? false,
      });
    } else {
      setSelectedLocation(null);
      setFormData({
        name: '',
        address: '',
        description: '',
        latitude: '',
        longitude: '',
        traffic_frequency: '',
        price_per_week: '',
        price_per_month: '',
        assigned_trailer_id: 'none',
        images: [],
        unavailable_from: '',
        unavailable_until: '',
        min_booking_lead_days: 28,
        has_existing_print: false,
      });
    }
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        price_per_week: parseFloat(formData.price_per_week),
        price_per_month: parseFloat(formData.price_per_month),
        assigned_trailer_id: formData.assigned_trailer_id === 'none' ? null : (formData.assigned_trailer_id || null),
        images: (formData.images || []).filter(url => url.trim() !== ''),
        unavailable_from: formData.unavailable_from || null,
        unavailable_until: formData.unavailable_until || null,
        min_booking_lead_days: parseInt(formData.min_booking_lead_days) || 28,
        has_existing_print: !!formData.has_existing_print,
      };

      const url = selectedLocation
        ? `${API_URL}/api/admin/locations/${selectedLocation.id}`
        : `${API_URL}/api/admin/locations`;
      
      const method = selectedLocation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchLocations();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/locations/${selectedLocation.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchLocations();
        setDeleteDialogOpen(false);
        setSelectedLocation(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const handleToggleAvailability = async (location) => {
    const newVal = !location.is_available;
    try {
      await fetch(`${API_URL}/api/admin/locations/${location.id}/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          is_available: newVal,
          ...(!newVal ? {} : { unavailable_from: null, unavailable_until: null })
        })
      });
      fetchLocations();
      fetchWaitlistStats();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const handleSetDateRange = async (locationId, from, until) => {
    try {
      await fetch(`${API_URL}/api/admin/locations/${locationId}/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ unavailable_from: from || null, unavailable_until: until || null })
      });
      fetchLocations();
    } catch (error) {
      console.error('Error setting date range:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">{t('admin.locations')}</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{locations.length} Standorte</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-location-button">
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.createLocation')}
        </Button>
      </div>

      {/* Locations Grid */}
      {locations.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Standorte</h3>
            <p className="text-[hsl(var(--muted-foreground))] mb-4">Erstellen Sie Ihren ersten Standort.</p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Standort erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {locations.map((location, index) => (
            <motion.div
              key={location.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="card-shadow overflow-hidden">
                {location.images && location.images.length > 0 && (
                  <div className="h-36 overflow-hidden">
                    <img
                      src={location.images[0]}
                      alt={location.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-[hsl(var(--primary)/0.1)] rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[hsl(var(--primary))]" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{location.name}</h3>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{location.address}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(location)} data-testid={`edit-location-${location.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedLocation(location); setDeleteDialogOpen(true); }} data-testid={`delete-location-${location.id}`}>
                        <Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" />
                      </Button>
                    </div>
                  </div>
                  
                  {location.description && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{location.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span className="text-sm">
                        <span className="font-medium">CHF {formatPrice(location.price_per_week)}</span>/Woche
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span className="text-sm">
                        <span className="font-medium">CHF {formatPrice(location.price_per_month)}</span>/Monat
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {location.trailer ? (
                      <Badge variant="secondary" className="flex items-center">
                        <Truck className="w-3 h-3 mr-1" />
                        {location.trailer.name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[hsl(var(--chart-3))]">
                        {t('admin.noTrailerAssigned')}
                      </Badge>
                    )}
                    {location.traffic_frequency && (
                      <Badge variant="secondary">{location.traffic_frequency}</Badge>
                    )}
                    {!location.is_active && (
                      <Badge variant="destructive">Inaktiv</Badge>
                    )}
                  </div>

                  {/* Availability Controls */}
                  <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Verfügbarkeit</span>
                        {waitlistStats[location.id] > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium" data-testid={`waitlist-count-${location.id}`}>
                            <Bell className="w-3 h-3" />
                            {waitlistStats[location.id]} Interessent{waitlistStats[location.id] > 1 ? 'en' : ''}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleAvailability(location); }}
                        className="flex items-center gap-1.5 text-sm"
                        data-testid={`toggle-availability-${location.id}`}
                      >
                        {location.is_available !== false ? (
                          <>
                            <ToggleRight className="w-6 h-6 text-[hsl(var(--chart-2))]" />
                            <span className="text-[hsl(var(--chart-2))] font-medium">Verfügbar</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
                            <span className="text-[hsl(var(--destructive))] font-medium">Nicht verfügbar</span>
                          </>
                        )}
                      </button>
                    </div>
                    {/* Date Range for temporary block */}
                    {(location.unavailable_from || location.unavailable_until) && (
                      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        <CalendarOff className="w-3.5 h-3.5" />
                        <span>Gesperrt: {location.unavailable_from || '–'} bis {location.unavailable_until || '–'}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleSetDateRange(location.id, null, null); }}
                          className="text-[hsl(var(--destructive))] underline ml-1"
                        >
                          Aufheben
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedLocation ? t('admin.editLocation') : t('admin.createLocation')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('admin.locationName')} *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="z.B. Wetzikon Zentrum"
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('admin.locationAddress')} *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="z.B. Bahnhofstrasse 1, 8620 Wetzikon"
                  data-testid="location-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  data-testid="location-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Breitengrad *</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="0.0001"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    required
                    placeholder="47.3266"
                    data-testid="location-latitude-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">Längengrad *</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="0.0001"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    required
                    placeholder="8.7983"
                    data-testid="location-longitude-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="traffic_frequency">{t('admin.locationTraffic')}</Label>
                <Input
                  id="traffic_frequency"
                  name="traffic_frequency"
                  value={formData.traffic_frequency}
                  onChange={handleInputChange}
                  placeholder="z.B. 15'000 Fahrzeuge/Tag"
                  data-testid="location-traffic-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_per_week">{t('admin.locationPriceWeek')} *</Label>
                  <Input
                    id="price_per_week"
                    name="price_per_week"
                    type="number"
                    step="0.01"
                    value={formData.price_per_week}
                    onChange={handleInputChange}
                    required
                    placeholder="350"
                    data-testid="location-price-week-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_month">{t('admin.locationPriceMonth')} *</Label>
                  <Input
                    id="price_per_month"
                    name="price_per_month"
                    type="number"
                    step="0.01"
                    value={formData.price_per_month}
                    onChange={handleInputChange}
                    required
                    placeholder="1200"
                    data-testid="location-price-month-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assigned_trailer_id">{t('admin.assignTrailer')}</Label>
                <Select
                  value={formData.assigned_trailer_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_trailer_id: value }))}
                >
                  <SelectTrigger data-testid="location-trailer-select">
                    <SelectValue placeholder="Anhänger auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Anhänger</SelectItem>
                    {trailers.map((trailer) => (
                      <SelectItem key={trailer.id} value={trailer.id}>
                        {trailer.name} ({trailer.size_m2}m²)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bilder (URLs)</Label>
                <div className="space-y-2">
                  {(formData.images || []).map((url, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={url}
                        onChange={(e) => {
                          const newImages = [...(formData.images || [])];
                          newImages[idx] = e.target.value;
                          setFormData(prev => ({ ...prev, images: newImages }));
                        }}
                        placeholder="https://example.com/bild.jpg"
                        className="flex-1"
                        data-testid={`location-image-url-${idx}`}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const newImages = (formData.images || []).filter((_, i) => i !== idx);
                        setFormData(prev => ({ ...prev, images: newImages }));
                      }}>
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ''] }));
                  }} data-testid="location-add-image-button">
                    + Bild-URL hinzufügen
                  </Button>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Fügen Sie URLs von Bildern hinzu (z.B. von Unsplash oder eigenen Hosting-Diensten)
                </p>
              </div>

              {/* Temporary Date Block */}
              {selectedLocation && (
                <div className="space-y-3 pt-4 border-t border-[hsl(var(--border))]">
                  <Label className="flex items-center gap-2">
                    <CalendarOff className="w-4 h-4" />
                    Temporäre Sperrung (optional)
                  </Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Standort für einen bestimmten Zeitraum sperren. Ausserhalb dieses Zeitraums ist der Standort automatisch wieder verfügbar.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Gesperrt von</Label>
                      <Input
                        type="date"
                        value={formData.unavailable_from || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, unavailable_from: e.target.value || '' }))}
                        data-testid="location-unavailable-from"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Gesperrt bis</Label>
                      <Input
                        type="date"
                        value={formData.unavailable_until || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, unavailable_until: e.target.value || '' }))}
                        data-testid="location-unavailable-until"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Buchungsregeln */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-[hsl(var(--foreground))]">Buchungsregeln</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Mindestvorlauf (Tage)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="90"
                      value={formData.min_booking_lead_days}
                      onChange={(e) => setFormData(prev => ({ ...prev, min_booking_lead_days: parseInt(e.target.value) || 0 }))}
                      data-testid="location-min-lead-days"
                    />
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Buchung muss mind. X Tage im Voraus erfolgen</p>
                  </div>
                  <div className="space-y-1 flex items-start gap-3 pt-5">
                    <input
                      type="checkbox"
                      id="has_existing_print"
                      checked={formData.has_existing_print}
                      onChange={(e) => setFormData(prev => ({ ...prev, has_existing_print: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      data-testid="location-has-print"
                    />
                    <div>
                      <label htmlFor="has_existing_print" className="text-xs font-medium cursor-pointer">Druck vorhanden</label>
                      <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Kein Express-Zuschlag, kurzfristige Buchung möglich</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving} data-testid="location-save-button">
                {saving ? 'Speichern...' : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Standort löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Standort "{selectedLocation?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[hsl(var(--destructive))]">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminLocations;
